import { useCallback } from "react";

import { getVariableHtml, StaticVariableTag, TicketingFormCategory } from "@simplyk/common";
import { inferRouterInputs } from "@trpc/server";
import { v4 } from "uuid";

import { useCurrentUserContext } from "../../../../common/contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../common/contexts/LocaleContext";
import type { AppRouterType } from "../../../../common/helpers/trpc";
import { useTranslate } from "../../../../common/hooks/useTranslate";

import { getDefaultFormColor } from "@/features/LiveFormEditor/common/defaultFormColorByCategory";
import { getTicketingThankYouEmailTemplates } from "@/features/LiveFormEditor/common/getTicketingThankYouEmailTemplates";
import { getPrefilledTitle } from "@/features/LiveFormEditor/common/prefillDataBuilders/getPrefilledTitle";

const FORM_WITHOUT_QR_CODE = [TicketingFormCategory.Shop];
const DEFAULT_SWEEPSTAKES_SEATS = 10000;
const FORM_WITHOUT_EXTRA_DONATION = [TicketingFormCategory.Auction];
type CreateTicketingInput = inferRouterInputs<AppRouterType>["createDraftTicketing"];

export const useCreateTicketingInput = () => {
  const { t } = useTranslate();
  const { currentUser, organization } = useCurrentUserContext();
  const { locale } = useLocaleContext();

  return useCallback(
    (category: TicketingFormCategory): CreateTicketingInput => {
      const emailTemplates = getTicketingThankYouEmailTemplates({
        category,
        t,
        organization,
        currentUser,
      });

      const buildEmailBody = (emailBody: string) =>
        t("dashboard", "ticketing.defaultFormEmailBody1") +
        getVariableHtml({ text: t("dashboard", "common.firstName"), tag: StaticVariableTag.FirstName }) +
        emailBody +
        t("dashboard", "ticketing.defaultFormEmailBody3", {
          firstName: currentUser?.firstName || "",
          lastName: currentUser?.lastName || "",
          email: currentUser?.email || "",
        });

      const defaultBidLoserEmailObject = t("dashboard", "ticketing.defaultFormEmailObject.auction.loser");
      const defaultBidLoserEmailBody = buildEmailBody(t("dashboard", "ticketing.defaultFormEmailBody2.auction.loser"));

      const prefilledTitle = getPrefilledTitle(category, t, organization);

      return {
        id: v4(),
        locale,
        eventTimezone: null,
        address: null,
        seats: category === TicketingFormCategory.Sweepstakes ? DEFAULT_SWEEPSTAKES_SEATS : null,
        formCategory: category,
        color: organization?.color || getDefaultFormColor(category),
        generateQrCode: FORM_WITHOUT_QR_CODE.includes(category) ? false : true,
        logo: null,
        duplicateLogo: true,
        banner: null,
        bannerPdf: null,
        bannerVideoUrl: null,
        extraDonation: !FORM_WITHOUT_EXTRA_DONATION.includes(category),
        chequeMinimumAmount: null,
        askForAddress: false,
        ticketingFields: [
          {
            locale,
            title: prefilledTitle,
            description: "",
            emailObject: emailTemplates.emailObject,
            emailContent: emailTemplates.emailContent,
            ...(category === TicketingFormCategory.Auction && {
              bidLoserEmailObject: defaultBidLoserEmailObject,
              bidLoserEmailContent: defaultBidLoserEmailBody,
            }),
          },
        ],
        rates: [],
        questionsPerParticipant: [],
        questionsPerOrder: [],
        recurrences: [],
        notificationEmails: currentUser?.email ? [currentUser.email] : null,
        isOnboardingForm: false,
        liveFormEditorStep: "title",
      };
    },
    [currentUser, locale, organization, t]
  );
};
